import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Grid,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import RepeatIcon from '@mui/icons-material/Repeat';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import dayjs, { Dayjs } from 'dayjs';
import { Task } from '../types';
import { tasksAPI } from '../services/api';

interface TaskSchedulerProps {
  storeId?: string;
  onTaskClick: (task: Task) => void;
  onDayClick: (date: string) => void;
  onTemplateClick?: (task: Task) => void;
  onTemplateDelete?: (taskId: number) => void;
  refreshKey?: number;
}

// Status colors
const STATUS_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  todo: { bg: '#fff3e0', border: '#ff9800', text: '#7a5d00' },
  in_progress: { bg: '#e3f2fd', border: '#1976d2', text: '#0d47a1' },
  done: { bg: '#e8f5e9', border: '#4caf50', text: '#1b5e20' },
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TaskScheduler: React.FC<TaskSchedulerProps> = ({
  storeId,
  onTaskClick,
  onDayClick,
  onTemplateClick,
  onTemplateDelete,
  refreshKey,
}) => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [tasks, setTasks] = useState<Task[]>([]);
  const [recurringTemplates, setRecurringTemplates] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  // Manual generate dialog state
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generateDate, setGenerateDate] = useState<Dayjs | null>(dayjs());
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<{ success: boolean; message: string } | null>(null);

  const year = currentDate.year();
  const month = currentDate.month();

  // Load tasks for the month and recurring templates
  useEffect(() => {
    const loadTasks = async () => {
      setLoading(true);
      try {
        const [tasksData, templatesData] = await Promise.all([
          tasksAPI.getCalendarTasks(year, month, storeId),
          tasksAPI.getRecurringTasks(storeId),
        ]);
        setTasks(tasksData);
        setRecurringTemplates(templatesData);
      } catch (error) {
        console.error('Error loading calendar tasks:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTasks();
  }, [year, month, storeId, refreshKey]);

  // Generate calendar grid
  const calendarGrid = useMemo(() => {
    const firstDayOfMonth = dayjs(new Date(year, month, 1));
    const lastDayOfMonth = dayjs(new Date(year, month + 1, 0));
    const startDay = firstDayOfMonth.day(); // 0 = Sunday
    const daysInMonth = lastDayOfMonth.date();

    const today = dayjs();

    const cells: Array<{
      day: number | null;
      date: string | null;
      isCurrentMonth: boolean;
      isToday: boolean;
      tasks: Task[];
    }> = [];

    // Fill empty cells before first day
    for (let i = 0; i < startDay; i++) {
      cells.push({
        day: null,
        date: null,
        isCurrentMonth: false,
        isToday: false,
        tasks: [],
      });
    }

    // Fill days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = dayjs(new Date(year, month, day)).format('YYYY-MM-DD');
      const isToday = today.format('YYYY-MM-DD') === dateStr;

      // Find tasks that overlap with this day
      const dayTasks = tasks.filter((task) => {
        const taskStart = task.instance_date || task.start_date;
        const taskEnd = task.end_date;

        if (!taskStart) return false;

        const startStr = dayjs(taskStart).format('YYYY-MM-DD');

        if (taskEnd) {
          const endStr = dayjs(taskEnd).format('YYYY-MM-DD');
          return dateStr >= startStr && dateStr <= endStr;
        }

        return dateStr === startStr;
      });

      cells.push({
        day,
        date: dateStr,
        isCurrentMonth: true,
        isToday,
        tasks: dayTasks,
      });
    }

    // Fill remaining cells to complete the grid (6 weeks = 42 cells)
    while (cells.length < 42) {
      cells.push({
        day: null,
        date: null,
        isCurrentMonth: false,
        isToday: false,
        tasks: [],
      });
    }

    // Split into weeks
    const weeks: typeof cells[] = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }

    // Remove empty trailing weeks
    while (weeks.length > 0 && weeks[weeks.length - 1].every(cell => !cell.isCurrentMonth)) {
      weeks.pop();
    }

    return weeks;
  }, [year, month, tasks]);

  const goToPreviousMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'));
  };

  const goToNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'));
  };

  const goToToday = () => {
    setCurrentDate(dayjs());
  };

  // Reload tasks after generating
  const reloadTasks = async () => {
    try {
      const [tasksData, templatesData] = await Promise.all([
        tasksAPI.getCalendarTasks(year, month, storeId),
        tasksAPI.getRecurringTasks(storeId),
      ]);
      setTasks(tasksData);
      setRecurringTemplates(templatesData);
    } catch (error) {
      console.error('Error reloading tasks:', error);
    }
  };

  // Handle manual task generation
  const handleGenerateTasks = async () => {
    if (!generateDate) return;

    setGenerating(true);
    setGenerateResult(null);

    try {
      const result = await tasksAPI.generateForDate(
        generateDate.format('YYYY-MM-DD'),
        storeId
      );

      if (result.success) {
        setGenerateResult({
          success: true,
          message: `Generated ${result.totalGenerated} task(s) up to ${generateDate.format('DD/MM/YYYY')}`
        });
        // Reload tasks to show newly generated ones
        await reloadTasks();
      } else {
        setGenerateResult({
          success: false,
          message: 'Failed to generate tasks'
        });
      }
    } catch (error) {
      console.error('Error generating tasks:', error);
      setGenerateResult({
        success: false,
        message: 'Error generating tasks. Please try again.'
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleCloseGenerateDialog = () => {
    setGenerateDialogOpen(false);
    setGenerateResult(null);
    setGenerateDate(dayjs());
  };

  return (
    <Box>
      {/* Calendar Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={goToPreviousMonth}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 180, textAlign: 'center' }}>
            {currentDate.format('MMMM YYYY')}
          </Typography>
          <IconButton onClick={goToNextMonth}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={goToToday}>
            Today
          </Button>
          <Button
            variant="contained"
            size="small"
            color="secondary"
            startIcon={<PlayArrowIcon />}
            onClick={() => setGenerateDialogOpen(true)}
          >
            Generate Tasks
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          {/* Weekday Headers */}
          <Grid container sx={{ bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}>
            {WEEKDAYS.map((day) => (
              <Grid item xs key={day} sx={{ py: 1, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar Grid */}
          {calendarGrid.map((week, weekIndex) => (
            <Grid
              container
              key={weekIndex}
              sx={{ borderBottom: weekIndex < calendarGrid.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
            >
              {week.map((cell, cellIndex) => (
                <Grid
                  item
                  xs
                  key={cellIndex}
                  sx={{
                    minHeight: 120,
                    borderRight: cellIndex < 6 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: cell.isCurrentMonth ? '#fff' : 'grey.50',
                    p: 0.5,
                  }}
                >
                  {cell.isCurrentMonth && (
                    <>
                      {/* Day Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: cell.isToday ? 700 : 600,
                            color: cell.isToday ? 'primary.main' : 'text.primary',
                            bgcolor: cell.isToday ? 'primary.light' : 'transparent',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {cell.day}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => cell.date && onDayClick(cell.date)}
                          sx={{
                            width: 20,
                            height: 20,
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:hover': { bgcolor: 'primary.light' },
                          }}
                        >
                          <AddIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>

                      {/* Tasks */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {cell.tasks.slice(0, 3).map((task) => {
                          const statusColor = STATUS_COLORS[task.status] || STATUS_COLORS.todo;
                          const isOverdue = task.is_overdue;

                          return (
                            <Box
                              key={task.id}
                              onClick={() => onTaskClick(task)}
                              sx={{
                                bgcolor: isOverdue ? '#ffebee' : statusColor.bg,
                                border: `1px solid ${isOverdue ? '#f44336' : statusColor.border}`,
                                borderRadius: 1,
                                px: 0.5,
                                py: 0.25,
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 },
                                overflow: 'hidden',
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.65rem',
                                  color: isOverdue ? '#c62828' : statusColor.text,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.25,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {task.parent_task_id && <RepeatIcon sx={{ fontSize: 10 }} />}
                                {task.title}
                              </Typography>
                            </Box>
                          );
                        })}
                        {cell.tasks.length > 3 && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontSize: '0.6rem', pl: 0.5 }}
                          >
                            +{cell.tasks.length - 3} more
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Grid>
              ))}
            </Grid>
          ))}
        </Paper>
      )}

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
        {Object.entries(STATUS_COLORS).map(([status, colors]) => (
          <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
              {status.replace('_', ' ')}
            </Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              bgcolor: '#ffebee',
              border: '1px solid #f44336',
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">Overdue</Typography>
        </Box>
      </Box>

      {/* Recurring Templates Section */}
      {recurringTemplates.length > 0 && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <RepeatIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Recurring Templates
            </Typography>
            <Chip label={recurringTemplates.length} size="small" color="primary" variant="outlined" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These templates automatically create task instances. Edit to change future tasks, or delete to stop the schedule.
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List dense disablePadding>
            {recurringTemplates.map((template) => {
              const frequencyLabel = template.recurring_config?.frequency === 'daily'
                ? 'Daily'
                : template.recurring_config?.frequency === 'bidaily'
                  ? 'Every 2 days'
                  : template.recurring_config?.frequency === 'weekly'
                    ? `Weekly (${(template.recurring_config?.weeklyDays || []).map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')})`
                    : 'Recurring';

              return (
                <ListItem
                  key={template.id}
                  sx={{
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': { bgcolor: '#e0e0e0' },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {template.title}
                        </Typography>
                        <Chip
                          label={frequencyLabel}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 18 }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {template.assignee_name || 'Unassigned'} • {template.store_name} • Started {dayjs(template.start_date).format('DD/MM/YYYY')}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      onClick={() => onTemplateClick?.(template)}
                      title="Edit template"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        if (window.confirm(`Delete recurring template "${template.title}"?\n\nThis will also delete all future instances.`)) {
                          onTemplateDelete?.(template.id!);
                        }
                      }}
                      title="Delete template"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}

      {/* Generate Tasks Dialog */}
      <Dialog open={generateDialogOpen} onClose={handleCloseGenerateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Recurring Tasks</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate task instances from recurring templates up to a specific date.
            This is useful for pre-creating tasks for upcoming days.
          </Typography>

          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Note: Tasks are automatically generated daily at 1:00 AM IST.
          </Typography>

          <Box sx={{ mt: 2 }}>
            <DatePicker
              label="Generate tasks up to"
              value={generateDate}
              onChange={(date) => setGenerateDate(date)}
              slotProps={{ textField: { fullWidth: true } }}
              format="DD/MM/YYYY"
              minDate={dayjs()}
            />
          </Box>

          {storeId && storeId !== 'all' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Will generate for current store filter only
            </Typography>
          )}

          {generateResult && (
            <Alert
              severity={generateResult.success ? 'success' : 'error'}
              sx={{ mt: 2 }}
            >
              {generateResult.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGenerateDialog}>Close</Button>
          <Button
            variant="contained"
            onClick={handleGenerateTasks}
            disabled={generating || !generateDate}
            startIcon={generating ? <CircularProgress size={16} /> : <PlayArrowIcon />}
          >
            {generating ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskScheduler;
